import { ConfirmationModal } from "@shared/components/ConfirmationModal";
import { useFormatDate } from "@shared/hooks/useFormatDate";
import { useProfile } from "@shared/hooks/useProfile";
import theme from "@shared/utils/themeColors";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { Link, useLocation, useNavigate } from "react-router-dom";
import EditMenu from "../../../../components/DropdownEditMenu";
import { usePostStore } from "../../store/postStore";
import { Post, PostType } from "../../types/post.types";
import { MediaGallery } from "../media/MediaGallery";
import { PollDisplay } from "@features/polls";
import { PostActions } from "./PostActions";
import { PostComments } from "./PostComments";
import SharePostModal from "./SharePostModal";

interface PostProps {
  post: Post;
}

export function SinglePost({ post }: PostProps) {
  const { user, getProfileThumbnail } = useProfile();
  const isAuthor = user?.uid === post.author?.id;
  const navigate = useNavigate();
  const location = useLocation();
  const { formatRelativeTime } = useFormatDate();
  const { deletePost, isDeleting } = usePostStore();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const handleDeletePost = async () => {
    await deletePost(post.id);
    toast.success("Post deleted successfully");

    // If we're in the post detail view, redirect to feed
    if (location.pathname.includes("/post/")) {
      navigate("/community/feed");
    }
    setShowDeleteConfirmation(false);
  };

  const handleShare = async (content?: string) => {
    try {
      await usePostStore.getState().sharePost(post.id!, content);
      setShowShareModal(false);
    } catch (error) {
      console.error("Error sharing post:", error);
    }
  };

  return (
    <>
      <div className={`${theme.bg.primary} shadow-sm rounded-xl p-5`}>
        {/* Post Header */}
        <div className="flex items-start space-x-3 mb-3 items-center">
          <Link to={`/community/profile/${post.author.id}`}>
            <img
              className="rounded-full"
              src={getProfileThumbnail(post.author)}
              width="40"
              height="40"
              alt={post.author.name}
            />
          </Link>
          <div className="grow">
            <div className="flex items-center space-x-2">
              <Link
                to={`/community/profile/${post.author.id}`}
                className={`text-sm font-semibold ${theme.text.primary} ${theme.text.brandHover}`}
              >
                {post.author.name}
              </Link>
              <span className={`text-sm ${theme.text.muted}`}>•</span>
              <span className={`text-sm ${theme.text.muted}`}>
                <Link
                  to={`/community/post/${post.id}`}
                  className={`${theme.text.brandHover}`}
                >
                  {formatRelativeTime(post.createdAt)}
                </Link>
              </span>
            </div>
          </div>
          {post.author.id === user?.uid && (
            <EditMenu
              align="right"
              className="relative top-0 right-0 inline-flex"
            >
              <li>
                <button
                  className={`font-medium text-sm ${theme.text.error} hover:text-red-600 flex py-1 px-3 items-center disabled:opacity-50`}
                  onClick={() => {
                    setShowDeleteConfirmation(true);
                  }}
                  disabled={isDeleting}
                >
                  <svg
                    className={`w-4 h-4 fill-current ${theme.text.error} shrink-0`}
                    viewBox="0 0 16 16"
                  >
                    <path d="M5 7h2v6H5V7zm4 0h2v6H9V7zm3-6v2h4v2h-1v10c0 .6-.4 1-1 1H2c-.6 0-1-.4-1-1V5H0V3h4V1c0-.6.4-1 1-1h6c.6 0 1 .4 1 1zM6 2v1h4V2H6zm7 3H3v9h10V5z" />
                  </svg>
                  <span className="ml-2">
                    {isDeleting ? "Deleting..." : "Delete Post"}
                  </span>
                </button>
              </li>
            </EditMenu>
          )}
        </div>

        {/* Post Content */}
        <div className="space-y-3">
          {/* Shared Post Info */}

          {/* Share Comment */}
          {post.sharedBy && post.content && (
            <div className={`text-sm ${theme.text.primary} space-y-2`}>
              {post.content}
            </div>
          )}

          {post.sharedBy && (
            <div
              className={`flex items-center space-x-1 text-xs ${theme.text.muted}`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                />
              </svg>
              <span>Shared from</span>
              <Link
                to={`/community/profile/${post.sharedPost.id}`}
                className={`font-medium text-gray-900 dark:text-gray-100 ${theme.text.brandHover}`}
              >
                {post.sharedPost.author.name}
              </Link>
              <span className={`text-sm ${theme.text.muted}`}>•</span>
              <Link
                to={`/community/post/${post.sharedPost.id}`}
                className={`font-medium text-gray-900 dark:text-gray-100 ${theme.text.brandHover}`}
              >
                View original
              </Link>
            </div>
          )}

          {/* Shared Post Content */}
          {post.sharedPost && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-start space-x-3 mb-3">
                <Link to={`/community/profile/${post.sharedPost.author?.id}`}>
                  <img
                    className="rounded-full"
                    src={getProfileThumbnail(post.sharedPost.author)}
                    width="32"
                    height="32"
                    alt={post.sharedPost.author?.name}
                  />
                </Link>
                <div>
                  <Link
                    to={`/community/profile/${post.sharedPost.author?.id}`}
                    className={`font-semibold text-gray-900 dark:text-gray-100 ${theme.text.brandHover}`}
                  >
                    {post.sharedPost.author?.name}
                  </Link>
                  <div className={`mt-1 text-sm ${theme.text.primary}`}>
                    {post.sharedPost.content}
                  </div>
                  {post.sharedPost.media &&
                    post.sharedPost.media.length > 0 && (
                      <div className="mt-3">
                        <MediaGallery media={post.sharedPost.media} />
                      </div>
                    )}
                </div>
              </div>
            </div>
          )}

          {/* Original Post Content (if not a share) */}
          {!post.sharedPost && (
            <>
              {/* Text Content */}
              <div
                className={`text-sm ${theme.text.primary} space-y-2 whitespace-pre-line`}
              >
                {post.content}
              </div>

              {/* Media Content */}
              {post.media && post.media.length > 0 && (
                <MediaGallery media={post.media} />
              )}

              {/* Poll Content */}
              {post.type === PostType.POLL && post.poll && (
                <PollDisplay
                  poll={post.poll}
                  ownerType="post"
                  onPollChange={(poll) =>
                    usePostStore.getState().setPostPoll(post.id!, poll)
                  }
                />
              )}
            </>
          )}
        </div>

        {/* Post Actions */}
        <PostActions post={post} onShare={() => setShowShareModal(true)} />

        {/* Comments Section */}
        <PostComments post={post} />
      </div>

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeletePost}
        isLoading={isDeleting}
        title="Delete Post"
        message="Are you sure you want to delete this post? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />

      <SharePostModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        post={post.sharedPost || post}
        onShare={handleShare}
      />
    </>
  );
}
